import { getCsrfToken } from '../utils/helpers.js';
import { bookingFlow } from '../components/bookingFlow.js';
import Alpine from 'alpinejs';
import {
    dateRangeExceedsMaxBookingDate,
    maxBookingDate,
} from '../utils/bookingDatePolicy.js';
import {
    rareFindImpression,
    recentBookingCueCheckoutStarted,
    nearbyDateSuggestionsImpression,
    nearbyDateSuggestionSelected,
} from '../utils/dataLayer.js';
import { formatNearbyDateRange } from '../utils/nearbyDateSuggestions.js';
import { shouldShowRareFindCue } from '../utils/rareFindCue.js';

export default function listingBooking(options = {}) {
    return {
        ...bookingFlow.init(options),
        get debugPricing() {
            return Boolean(Alpine.store('user')?.canShowDebugFeatures);
        },

        get showRareFindCue() {
            return shouldShowRareFindCue(this);
        },

        // Dynamic pricing state
        nightlyRate: 0,
        originalNightlyRate: 0,
        total: 0,
        taxesAndFees: 0,
        minStayNights: 1,
        currency: 'USD',
        isCalculated: false,
        basePrice: options.basePrice || 0,
        quote: null,
        // Identifies the date range that produced the currently applied quote.
        quotedDateRangeKey: '',
        isSubmitting: false,
        invalidInitialDateMessage: '',
        rareFindMetric: null,
        rareFindImpressionKey: '',
        nearbyDateSuggestions: [],
        nearbyDatesLoading: false,
        nearbyDatesRequestKey: '',
        
        getMaxGuests() {
            const parsed = Number(options.maxGuests);
            return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
        },

        getTotalGuests() {
            const adults = Number.isFinite(Number(this.adults)) ? Number(this.adults) : 0;
            const children = Number.isFinite(Number(this.children)) ? Number(this.children) : 0;
            return adults + children;
        },

        validateGuestCount() {
            const maxGuests = this.getMaxGuests();
            if (!maxGuests) {
                return null;
            }

            const totalGuests = this.getTotalGuests();
            if (totalGuests > maxGuests) {
                const guestLabel = maxGuests === 1 ? 'guest' : 'guests';
                return `This property allows up to ${maxGuests} ${guestLabel}.`;
            }

            return null;
        },

        clearGuestCountError() {
            if (!this.errors?.general || !this.errors.general.startsWith('This property allows up to ')) {
                return;
            }

            const nextErrors = { ...(this.errors || {}) };
            delete nextErrors.general;
            this.errors = nextErrors;
        },
        
        init() {
            // Watch global search store for changes
            this.$watch('$store.search.dates', (val) => this.syncDates(val));
            this.$watch('$store.search.guests', (val) => this.syncGuests());
            this.$watch('$store.search.adults', (val) => this.syncGuests());
            this.$watch('$store.search.children', (val) => this.syncGuests());
            this.$watch('$store.search.pets', (val) => this.syncGuests());
            
            // Watch internal state changes to trigger pricing update
            this.$watch('adults', () => this.updatePricing());
            this.$watch('children', () => this.updatePricing());
            this.$watch('pets', () => this.updatePricing());
            
            // Initial sync from store (which is initialized from URL)
            const searchStore = Alpine.store('search');
            if (searchStore) {
                const urlState = this.getUrlBookingState();
                if (dateRangeExceedsMaxBookingDate(urlState.dates)) {
                    this.invalidInitialDateMessage = this.getBookingDateLimitMessage();
                    searchStore.dates = '';
                    this.removeDatesFromUrl();
                } else if (urlState.dates) {
                    searchStore.dates = urlState.dates;
                }
                if (urlState.adults !== null) {
                    searchStore.adults = urlState.adults;
                }
                if (urlState.children !== null) {
                    searchStore.children = urlState.children;
                }
                if (urlState.guests !== null) {
                    searchStore.guests = urlState.guests;
                } else if (urlState.adults !== null || urlState.children !== null) {
                    searchStore.guests = Math.max(1, (Number(searchStore.adults) || 0) + (Number(searchStore.children) || 0));
                }
                if (urlState.pets !== null) {
                    searchStore.pets = urlState.pets;
                }
                if (urlState.couponCode) {
                    this.couponCode = urlState.couponCode;
                }

                // If no dates set in store (not from URL), set default: Tomorrow -> +3 nights
                if (!searchStore.hasDates() && !this.invalidInitialDateMessage) {
                    this.skipNextReviewTracking = true;
                    const today = new Date();
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    const checkout = new Date(tomorrow);
                    checkout.setDate(tomorrow.getDate() + 3);
                    
                    const formatDate = (d) => {
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    };
                    
                    searchStore.dates = `${formatDate(tomorrow)} to ${formatDate(checkout)}`;
                }

                // If no guests set in store, set default: 2 (capped by maxGuests)
                if (searchStore.guests === 0) {
                    const max = options.maxGuests || 10;
                    const defaultGuests = Math.min(2, max);
                    searchStore.adults = defaultGuests;
                    // Trigger total guest update in store helper usually, but here we set distinct parts
                    // The store watcher or getter handles 'guests' total usually, but search.js has updateGuests()
                    // We should trigger that or manually set guests too if it doesn't auto-update
                    searchStore.guests = defaultGuests; 
                    // Note: search.js doesn't auto-watch adults/children to update guests, 
                    // it has an updateGuests() method but we are outside it.
                    // Ideally we call searchStore.updateGuests() if available or just set it.
                }

                if (searchStore.dates) {
                    this.syncDates(searchStore.dates);
                } else if (this.invalidInitialDateMessage) {
                    this.syncDates('');
                }
                this.syncGuests();
            }

            // Move the single form element to the correct viewport container
            this._moveToViewportContainer();
            window.matchMedia('(min-width: 1024px)').addEventListener('change', () => {
                this._moveToViewportContainer();
            });

            this._onMinStayEvent = (event) => {
                const detail = event?.detail || {};
                if (!detail.listingId || detail.listingId !== this.listingId) {
                    return;
                }

                const minNights = Math.max(1, Number(detail.minNights || 1));
                this.minStayNights = minNights;

                if (detail.state === 'violation') {
                    const nightLabel = minNights === 1 ? 'night' : 'nights';
                    this.errors = {
                        ...(this.errors || {}),
                        general: `Minimum stay is ${minNights} ${nightLabel}. Please select a longer range.`,
                    };
                    this.isCalculated = false;
                    return;
                }

                if (detail.state === 'clear') {
                    this.minStayNights = 1;
                }

                if (this.errors?.general && this.errors.general.startsWith('Minimum stay is ')) {
                    const nextErrors = { ...(this.errors || {}) };
                    delete nextErrors.general;
                    this.errors = nextErrors;
                }
            };
            window.addEventListener('listing:min-stay', this._onMinStayEvent);

            this._onGuestLimitEvent = (event) => {
                const maxGuests = Number(event?.detail?.maxGuests);
                if (!Number.isFinite(maxGuests) || maxGuests <= 0) {
                    return;
                }

                const guestLabel = maxGuests === 1 ? 'guest' : 'guests';
                this.errors = {
                    ...(this.errors || {}),
                    general: `This property allows up to ${maxGuests} ${guestLabel}.`,
                };
                this.isCalculated = false;
            };
            window.addEventListener('listing:guest-limit', this._onGuestLimitEvent);

            this.loadRareFindMetric();

        },

        getUrlBookingState() {
            const params = new URLSearchParams(window.location.search);
            const dates = options.initialDates || params.get('dates');
            const couponCode = options.couponCode || params.get('couponCode') || params.get('promo') || '';
            const parsePositiveInt = (value) => {
                if (value === null || value === '') return null;
                const parsed = parseInt(value, 10);
                return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
            };

            const adults = parsePositiveInt(options.initialAdults ?? params.get('adults'));
            const children = parsePositiveInt(options.initialChildren ?? params.get('children'));
            const guests = parsePositiveInt(options.initialGuests ?? params.get('guests'));
            const pets = parsePositiveInt(options.initialPets ?? params.get('pets'));

            return {
                dates: dates && dates.includes(' to ') ? dates : '',
                adults,
                children,
                guests,
                pets: pets === null ? null : Math.max(0, Math.min(2, pets)),
                couponCode,
            };
        },
        

        syncDates(datesStr) {
            this.rareFindImpressionKey = '';
            this.nearbyDateSuggestions = [];
            this.nearbyDatesLoading = false;
            this.nearbyDatesRequestKey = '';
            const normalizedDates = typeof datesStr === 'string' ? datesStr.trim() : '';

            if (!normalizedDates.includes(' to ')) {
                // Flatpickr emits the new check-in by itself while a range is incomplete.
                this.checkIn = normalizedDates;
                this.checkOut = '';

                if (!normalizedDates && this.invalidInitialDateMessage) {
                    this.errors = { general: this.invalidInitialDateMessage };
                    this.isCalculated = false;
                    return;
                }
            } else {
                const [start, end] = normalizedDates.split(' to ');
                this.checkIn = start ? start.trim() : '';
                this.checkOut = end ? end.trim() : '';
            }

            this.invalidInitialDateMessage = '';

            // Clear stale availability errors immediately when user changes dates
            this.errors = this.checkIn && !this.checkOut
                ? { general: 'Please choose a check-out date.' }
                : {};

            this.updatePricing();
        },

        getDateRangeKey() {
            return this.checkIn && this.checkOut
                ? `${this.checkIn}|${this.checkOut}`
                : '';
        },

        hasBlockingErrors() {
            // Shared quote helpers keep cleared error keys as empty strings.
            // Coupon errors are informational because pricing falls back without the coupon.
            return Object.entries(this.errors || {}).some(([key, value]) => (
                key !== 'couponCode' && Boolean(value)
            ));
        },

        get canReserve() {
            const currentDateRangeKey = this.getDateRangeKey();

            // Quote updates can replace pricing or the quote ID without changing dates,
            // so submission must wait for both the range match and all mutations to settle.
            return Boolean(
                currentDateRangeKey
                && !this.loading
                && this.isCalculated
                && this.quoteId
                && this.quote
                && this.quotedDateRangeKey === currentDateRangeKey
                && this.availability?.available !== false
                && !this.hasBlockingErrors()
            );
        },

        invalidateQuoteState() {
            if (this.pricingTimeout) {
                clearTimeout(this.pricingTimeout);
                this.pricingTimeout = null;
            }

            // Advance both generations so estimate and inherited quote-update
            // responses cannot restore state for the previous selection.
            this._pricingRequestId = (this._pricingRequestId || 0) + 1;
            this.quoteUpdateRequestId = (this.quoteUpdateRequestId || 0) + 1;

            if (this._estimateAbortController) {
                this._estimateAbortController.abort('dates-changed');
                this._estimateAbortController = null;
            }

            if (this._nearbyDatesAbortController) {
                this._nearbyDatesAbortController.abort('dates-changed');
                this._nearbyDatesAbortController = null;
            }

            this.loading = false;
            this.isCalculated = false;
            this.quote = null;
            this.quoteId = null;
            this.quotedDateRangeKey = '';
            this.availability = null;
            this.paymentSchedule = null;
            this.nightlyRate = 0;
            this.total = 0;
            this.taxesAndFees = 0;
            this.nearbyDateSuggestions = [];
            this.nearbyDatesLoading = false;
            this.nearbyDatesRequestKey = '';
        },
        
        syncGuests() {
            const searchStore = Alpine.store('search');
            if (!searchStore) return;
            
            const adults = Number(searchStore.adults);
            const children = Number(searchStore.children);
            const guests = Number(searchStore.guests);
            const pets = Number(searchStore.pets);

            this.adults = Number.isFinite(adults) && adults >= 0
                ? adults
                : (Number.isFinite(guests) && guests >= 0 ? guests : 0);
            this.children = Number.isFinite(children) && children >= 0 ? children : 0;
            this.pets = Number.isFinite(pets) ? Math.max(0, Math.min(2, Math.floor(pets))) : 0;

            const guestCountError = this.validateGuestCount();
            if (guestCountError) {
                this.errors = {
                    ...(this.errors || {}),
                    general: guestCountError,
                };
                this.isCalculated = false;
                return;
            }

            this.clearGuestCountError();
        },
        
        async updatePricing() {
            this.invalidateQuoteState();
            const requestId = this._pricingRequestId;
            const dateRangeKey = this.getDateRangeKey();

            if (!dateRangeKey) {
                this.updateUrl();
                return;
            }

            this.pricingTimeout = setTimeout(async () => {
                this.pricingTimeout = null;

                if (requestId !== this._pricingRequestId || dateRangeKey !== this.getDateRangeKey()) {
                    return;
                }

                if (!this.checkIn || !this.checkOut) {
                    this.isCalculated = false;
                    this.nightlyRate = 0;
                    this.originalNightlyRate = 0;
                    this.quote = null;
                    this.updateUrl();
                    return;
                }

                // Calculate nights count locally to ensure availability
                const start = new Date(this.checkIn);
                const end = new Date(this.checkOut);
                const nightsCount = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
                
                // Reset state before fetching
                this.isCalculated = false;
                this.quote = null;
                this.errors = {};

                const guestCountError = this.validateGuestCount();
                if (guestCountError) {
                    this.errors = { general: guestCountError };
                    this.updateUrl();
                    return;
                }
                
                this.updateUrl();
                
                await this.estimate(requestId, dateRangeKey);


            }, 200);
        },

        async estimate(requestId = this._pricingRequestId, dateRangeKey = this.getDateRangeKey()) {
            if (!this.listingId || !this.checkIn || !this.checkOut) {
                this.errors = { general: 'Please select dates and ensure listing is selected.' };
                return;
            }

            if (this._estimateAbortController) {
                this._estimateAbortController.abort('superseded');
            }

            this.loading = true;
            this.errors = {};
            const abortController = new AbortController();
            this._estimateAbortController = abortController;

            try {
                // The estimate endpoint disables CSRF server-side, so avoid
                // fetching a token during automatic pricing refreshes.
                const headers = {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                };

                const buildBody = (couponCode = this.couponCode || null) => JSON.stringify({
                    listingId: this.listingId,
                    checkIn: this.checkIn,
                    checkOut: this.checkOut,
                    adults: this.adults,
                    children: this.children,
                    infants: this.infants,
                    couponCode,
                });

                let response = await fetch('/guesty/booking/review', {
                    method: 'POST',
                    headers,
                    signal: abortController.signal,
                    body: buildBody(),
                });

                let data = await response.json();

                let couponErrors = null;
                if (!data.success && data.stage === 'coupon' && this.couponCode) {
                    couponErrors = {
                        couponCode: data.errors?.couponCode || data.errors?.general || 'Invalid promo code, please try again.',
                    };
                    response = await fetch('/guesty/booking/review', {
                        method: 'POST',
                        headers,
                        signal: abortController.signal,
                        body: buildBody(null),
                    });
                    data = await response.json();
                }

                // Never apply a quote produced for an earlier date selection.
                if (requestId !== this._pricingRequestId || dateRangeKey !== this.getDateRangeKey()) return;

                if (data.success) {
                    this.applyQuoteData(data.data);

                    if (this.availability && this.availability.available === false) {
                        this.errors = {
                            general: 'The selected dates are no longer available. Please choose different dates.',
                        };
                        this.isCalculated = false;
                        await this.loadNearbyDateSuggestions(requestId, dateRangeKey);
                        return;
                    }

                    const nights = Math.max(1, Number(this.quote?.nightsCount || 1));
                    const originalAccommodation = this.getOriginalAccommodationAmount();
                    const adjustedAccommodation = this.getAdjustedAccommodationAmount();
                    const taxesAndFees = Number(this.quote?.taxes || 0) + Number(this.quote?.fees || 0);

                    this.originalNightlyRate = originalAccommodation / nights;
                    this.nightlyRate = adjustedAccommodation / nights;
                    this.total = Number(this.quote?.total || 0);
                    this.taxesAndFees = taxesAndFees;
                    this.quotedDateRangeKey = dateRangeKey;
                    this.isCalculated = true;
                    this.maybeTrackRareFindImpression();
                    const verifiedStay = {
                        listingId: this.listingId,
                        checkIn: this.checkIn,
                        checkOut: this.checkOut,
                    };
                    window.__avariVerifiedListingStay = verifiedStay;
                    window.dispatchEvent(new CustomEvent('listing-availability-verified', {
                        detail: verifiedStay,
                    }));
                    if (couponErrors) {
                        this.errors = couponErrors;
                    }
                } else {
                    const hasErrors = data.errors && typeof data.errors === 'object' && Object.keys(data.errors).length > 0;
                    const errorMessage = hasErrors ? this.getFirstErrorMessage(data.errors) : '';
                    this.errors = hasErrors
                        ? { ...data.errors, general: errorMessage || 'Failed to get pricing. Please try again.' }
                        : { general: 'Failed to get pricing. Please try again.' };
                    this.isCalculated = false;
                    if (data.stage === 'availability') {
                        this.errors.general = 'The selected dates are no longer available. Please choose different dates.';
                        await this.loadNearbyDateSuggestions(requestId, dateRangeKey);
                    }
                }
            } catch (error) {
                if (requestId !== this._pricingRequestId || dateRangeKey !== this.getDateRangeKey()) return;
                if (error?.name === 'AbortError') return;
                this.errors = { general: 'Network error. Please try again.' };
                this.isCalculated = false;
            } finally {
                if (this._estimateAbortController === abortController) {
                    this._estimateAbortController = null;
                }
                if (requestId === this._pricingRequestId) {
                    this.loading = false;
                }
            }
        },

        async loadNearbyDateSuggestions(requestId, dateRangeKey) {
            if (!this.listingId || !this.checkIn || !this.checkOut) return;

            if (this._nearbyDatesAbortController) {
                this._nearbyDatesAbortController.abort('superseded');
            }

            const requestKey = `${this.listingId}:${this.checkIn}:${this.checkOut}:${this.getTotalGuests()}`;
            const controller = new AbortController();
            this._nearbyDatesAbortController = controller;
            this.nearbyDatesLoading = true;
            this.nearbyDatesRequestKey = requestKey;
            this.nearbyDateSuggestions = [];

            try {
                const params = new URLSearchParams({
                    listingId: this.listingId,
                    checkIn: this.checkIn,
                    checkOut: this.checkOut,
                    guests: String(Math.max(1, this.getTotalGuests())),
                });
                const response = await fetch(`/guesty/listing/nearby-dates?${params.toString()}`, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                    signal: controller.signal,
                });
                const payload = await response.json();

                if (
                    controller !== this._nearbyDatesAbortController
                    || requestId !== this._pricingRequestId
                    || dateRangeKey !== this.getDateRangeKey()
                    || requestKey !== this.nearbyDatesRequestKey
                ) {
                    return;
                }

                this.nearbyDateSuggestions = response.ok
                    && payload?.success
                    && Array.isArray(payload.data?.suggestions)
                    ? payload.data.suggestions.map((suggestion) => ({
                        ...suggestion,
                        label: formatNearbyDateRange(suggestion.checkIn, suggestion.checkOut),
                    }))
                    : [];
                if (this.nearbyDateSuggestions.length > 0) {
                    nearbyDateSuggestionsImpression(this, this.nearbyDateSuggestions);
                }
            } catch (error) {
                if (error?.name !== 'AbortError') {
                    console.warn('Nearby date suggestions unavailable:', error);
                }
            } finally {
                if (controller === this._nearbyDatesAbortController) {
                    this._nearbyDatesAbortController = null;
                    this.nearbyDatesLoading = false;
                }
            }
        },

        applyNearbyDateSuggestion(suggestion) {
            if (!suggestion?.checkIn || !suggestion?.checkOut) return;
            nearbyDateSuggestionSelected(this, suggestion);

            const dateInput = document.querySelector(
                '#avari-listing-booking-form input[name="dates"]'
            );
            if (typeof dateInput?._avariSetDateRange === 'function') {
                dateInput._avariSetDateRange(suggestion);
            }

            const searchStore = Alpine.store('search');
            if (searchStore) {
                searchStore.dates = `${suggestion.checkIn} to ${suggestion.checkOut}`;
            } else {
                this.syncDates(`${suggestion.checkIn} to ${suggestion.checkOut}`);
            }
        },

        async loadRareFindMetric() {
            if (!this.listingId) return;
            window.__avariRareFindState = { loaded: false, enabled: false };
            try {
                const params = new URLSearchParams({ listingId: this.listingId });
                if (new URLSearchParams(window.location.search).get('rareFindPreview') === '1') {
                    params.set('preview', '1');
                }
                const response = await fetch(`/guesty/listing/rare-find?${params.toString()}`, {
                    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                });
                const payload = await response.json();
                this.rareFindMetric = response.ok && payload.success ? payload.data : null;
                this.maybeTrackRareFindImpression();
            } catch {
                this.rareFindMetric = null;
            } finally {
                const state = {
                    loaded: true,
                    enabled: Boolean(this.rareFindMetric?.enabled),
                };
                window.__avariRareFindState = state;
                window.dispatchEvent(new CustomEvent('listing-rare-find-state', { detail: state }));
            }
        },

        maybeTrackRareFindImpression() {
            if (!this.showRareFindCue) return;
            const key = `${this.listingId}:${this.checkIn}:${this.checkOut}:${this.rareFindMetric.calculatedAt || ''}`;
            if (this.rareFindImpressionKey === key) return;
            this.rareFindImpressionKey = key;
            rareFindImpression(this);
        },

        _moveToViewportContainer() {
            const isLg = window.matchMedia('(min-width: 1024px)').matches;
            const targetId = isLg ? 'desktop-booking-form-container' : 'mobile-booking-form-container';
            const target = document.getElementById(targetId);
            if (target && this.$el.parentElement !== target) {
                target.appendChild(this.$el);
            }
        },

        updateUrl() {
            const url = new URL(window.location);
            // Keep the last complete range in the URL while a new selection is incomplete.
            if (this.checkIn && this.checkOut) {
                url.searchParams.set('dates', `${this.checkIn} to ${this.checkOut}`);
            }
            
            if (this.adults > 0) url.searchParams.set('adults', this.adults);
            if (this.children > 0) url.searchParams.set('children', this.children);
            if (this.pets > 0) {
                url.searchParams.set('pets', this.pets);
            } else {
                url.searchParams.delete('pets');
            }
            
            // Sync guests param for consistency with search bar
            const totalGuests = (parseInt(this.adults) || 1) + (parseInt(this.children) || 0);
            url.searchParams.set('guests', totalGuests);
            
            window.history.replaceState({}, '', url);
            window.__avariVerifiedListingStay = null;
            window.dispatchEvent(new CustomEvent('listing-booking-dates-changed', {
                detail: {
                    checkIn: this.checkIn || '',
                    checkOut: this.checkOut || '',
                },
            }));
        },

        removeDatesFromUrl() {
            const url = new URL(window.location);
            url.searchParams.delete('dates');
            window.history.replaceState({}, '', url);
        },

        getBookingDateLimitMessage() {
            const formattedMaxDate = maxBookingDate().toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            });

            return `Bookings are available through ${formattedMaxDate}. Please choose new dates.`;
        },

        getFirstErrorMessage(errors) {
            const values = [
                errors?.general,
                errors?.checkOut,
                errors?.checkIn,
                ...Object.values(errors || {}),
            ];

            for (const value of values) {
                if (typeof value === 'string' && value.trim()) return value;
                if (Array.isArray(value)) {
                    const message = value.find((item) => typeof item === 'string' && item.trim());
                    if (message) return message;
                }
            }

            return '';
        },
        
        async reserve(e) {
            this.isSubmitting = false;

            console.log('Reserve button clicked', {
                isCalculated: this.isCalculated,
                errors: this.errors,
                quoteId: this.quote?.quoteId,
                basePrice: this.basePrice
            });

            // The button binding is UX only; repeat the full guard before submitting.
            if (!this.canReserve) {
                console.log('Current selection does not have a valid quote');
                e.preventDefault();

                if (!this.hasBlockingErrors()) {
                    this.errors = {
                        general: !this.checkIn || !this.checkOut
                            ? 'Please select check-in and check-out dates to continue.'
                            : 'Please wait for updated pricing before reserving.',
                    };
                }
                return;
            }

            const attributionField = e.target?.querySelector('input[name="marketingAttribution"]');
            if (attributionField && window.__avariAttribution?.getPayload) {
                attributionField.value = JSON.stringify(window.__avariAttribution.getPayload());
            }

            e.preventDefault();
            this.isSubmitting = true;
            recentBookingCueCheckoutStarted(this);

            try {
                const csrf = await getCsrfToken(true);

                if (!csrf?.name || !csrf?.value) {
                    throw new Error('CSRF token not available');
                }

                let csrfField = e.target?.querySelector(`input[name="${csrf.name}"]`);
                if (!csrfField) {
                    csrfField = document.createElement('input');
                    csrfField.type = 'hidden';
                    csrfField.name = csrf.name;
                    e.target.appendChild(csrfField);
                }

                csrfField.value = csrf.value;
                e.target.submit();
            } catch (error) {
                console.error('Unable to refresh CSRF token before reserving:', error);
                this.isSubmitting = false;
                this.errors = { general: 'Unable to verify your booking form. Please refresh and try again.' };
            }
        },

        formatPrice(amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        }
    };
}
